import React from 'react';
import { IResource } from '@gettactic/api';
import { MapCanvasValue } from '@gettactic/map-builder/src/components/map/contexts/MapCanvasContext';
import { MapBuilderViewer } from '@gettactic/map-builder/src/MapBuilderViewer';
import { ReactSVG } from 'react-svg';
import { LoadingSpinner } from '@cmp-src/loadings/LoadingSpinner';
import { useBooleanState } from '@gettactic/helpers';
import { useMap } from '@webV2/map/MapProvider';

export interface FloorMapProps {
  floor: IResource;
  isEditing?: boolean;
  onMapLoaded?: (error: Error | null, svg?: SVGSVGElement) => void;
  onMapEditLoaded?: (error: Error | null, svg?: SVGSVGElement) => void;
  style?: React.CSSProperties;
  showLabel?: boolean;
  label?: string;
}

export const FloorMap: React.FC<FloorMapProps> = ({
  floor,
  isEditing = false,
  onMapLoaded,
  onMapEditLoaded,
  style,
  showLabel = false,
  label = ''
}) => {
  const { forceMapBuilder } = useMap();
  const mapData = floor.map_data
    ? (JSON.parse(floor.map_data) as MapCanvasValue)
    : undefined;

  const isMapLoaded = useBooleanState();

  const isMapBuilder = forceMapBuilder || (!floor.map_url && !!mapData);

  return (
    <>
      {isMapBuilder ? (
        <div
          style={{
            display: 'block',
            borderRadius: '8px',
            cursor: 'default',
            outline: 'none'
          }}
        >
          <div>
            <MapBuilderViewer
              floor={floor || undefined}
              resources={[]}
              interactiveZoneInfo={{}}
              highlightResource={undefined}
              mapEditPosition={null}
              ref={(svg) => {
                if (isMapLoaded.state) return;
                if (!svg) return;

                if (isEditing) {
                  onMapEditLoaded?.(null, svg);
                  return;
                }
                onMapLoaded?.(null, svg);
                isMapLoaded.true();
              }}
              style={style}
              useSvg
            />
          </div>
        </div>
      ) : (
        <ReactSVG
          afterInjection={(error, svg) => {
            if (isMapLoaded.state) return;

            if (isEditing) {
              onMapEditLoaded?.(error, svg);
              return;
            }
            onMapLoaded?.(error, svg);
            isMapLoaded.true();
          }}
          src={floor.map_url}
          loading={LoadingSpinner}
          style={style}
          key={`${floor.id}-${isEditing ? 'edit' : 'view'}`}
        />
      )}
      {/* LABEL */}
      {showLabel && (
        <div
          className={'pointer-events-none flex items-center justify-center'}
          style={{
            transform: 'rotateX(0deg)',
            transformStyle: 'preserve-3d'
          }}
        >
          <div
            className={
              'pointer-events-none mt-2 inline-block rounded-md border-1 border-neutral-400 bg-secondary px-2 py-1 text-center font-medium text-4xl text-neutral-800 shadow-lg'
            }
            style={{
              transform: 'rotateX(0deg)',
              transformStyle: 'preserve-3d'
            }}
          >
            {label || floor.name || ''}
          </div>
        </div>
      )}
    </>
  );
};
